import React, { useState } from 'react';
import { X, Phone, Calendar, Send, HelpCircle } from 'lucide-react';

export const SupportButton = ({ currentPatient, currentLocation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState('');
  const [patientName, setPatientName] = useState('');
  const [message, setMessage] = useState('');
  const [otherIssue, setOtherIssue] = useState('');
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Update patient name when showing the message form
  React.useEffect(() => {
    if (showMessageForm && currentPatient && currentPatient.name) {
      setPatientName(currentPatient.name);
    }
  }, [showMessageForm, currentPatient]);

  const handleIssueSelect = (issue) => {
    setSelectedIssue(issue);
    setShowMessageForm(true);
  };

  const handleOtherSelect = () => {
    setSelectedIssue('Other');
    setShowMessageForm(true);
  };

  const handleCall = () => {
    window.location.href = 'tel:+13108968032';
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/geoffrey-prefacehealth/geoffrey-15-minutes', '_blank');
  };

  const handleSendMessage = async () => {
    setIsSending(true);
    setSubmitStatus(null);

    const finalIssue = selectedIssue === 'Other' ? otherIssue : selectedIssue;

    try {
      const response = await fetch('/api/support/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          issue: finalIssue,
          patientName: patientName,
          message: message,
          location: currentLocation,
          patientId: currentPatient?.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send support request');
      }

      setSubmitStatus('success');
      setTimeout(() => {
        setIsOpen(false);
        setSelectedIssue('');
        setPatientName('');
        setMessage('');
        setOtherIssue('');
        setShowMessageForm(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error sending support request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedIssue('');
    setPatientName('');
    setMessage('');
    setOtherIssue('');
    setShowMessageForm(false);
    setSubmitStatus(null);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="px-3 py-2 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2"
        aria-label="Support"
      >
        <HelpCircle className="w-5 h-5" />
        <span className="hidden sm:inline">Support</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Support</h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {!showMessageForm ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-3">
                      How can we help you?
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Select an issue type or describe your problem
                    </p>

                    <div className="space-y-3">
                      <button
                        onClick={() => handleIssueSelect("Didn't send to EHR")}
                        className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-teal-500 transition-colors text-gray-700 font-medium"
                      >
                        Didn't send to EHR
                      </button>

                      <button
                        onClick={handleOtherSelect}
                        className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-teal-500 transition-colors text-gray-700 font-medium"
                      >
                        Other
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-3">
                      Need immediate help?
                    </h3>

                    <div className="space-y-3">
                      <button
                        onClick={handleCall}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        <Phone className="w-5 h-5" />
                        <span>Call Support: (310) 896-8032</span>
                      </button>

                      <button
                        onClick={handleBookCall}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors"
                      >
                        <Calendar className="w-5 h-5" />
                        <span>Book a Support Call</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">
                      {selectedIssue === 'Other' ? 'Other Issue' : selectedIssue}
                    </h3>
                    <button
                      onClick={() => {
                        setShowMessageForm(false);
                        setOtherIssue('');
                      }}
                      className="text-sm text-teal-600 hover:text-teal-700"
                    >
                      ← Back to issues
                    </button>
                  </div>

                  {selectedIssue === 'Other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Issue Type
                      </label>
                      <input
                        type="text"
                        value={otherIssue}
                        onChange={(e) => setOtherIssue(e.target.value)}
                        placeholder="Describe the type of issue"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patient Name
                    </label>
                    <input
                      type="text"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      placeholder="Enter patient name or leave blank"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {currentPatient ? `Current patient: ${currentPatient.name}` : 'No patient currently selected'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Details
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Provide any additional details..."
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                      Support request sent successfully!
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                      Failed to send support request. Please try again.
                    </div>
                  )}

                  <div className="space-y-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSendMessage}
                      disabled={isSending || (selectedIssue === 'Other' && !otherIssue.trim())}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5" />
                      <span>{isSending ? 'Sending...' : 'Send Support Request'}</span>
                    </button>

                    <button
                      onClick={handleCall}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Call Support: (310) 896-8032</span>
                    </button>

                    <button
                      onClick={handleBookCall}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors"
                    >
                      <Calendar className="w-5 h-5" />
                      <span>Book a Support Call</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
